import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  User, Link2, Copy, Plus, Trash2, Pencil, Instagram,
  MessageCircle, Send, Image, Package, ExternalLink, Eye,
  Facebook, Youtube, Ticket, Calendar
} from "lucide-react";

// Ícone do TikTok (não existe no lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Ícone do Mercado Livre (usando imagem)
const MercadoLivreIcon = ({ className }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_8900ab4b-3793-4410-9d57-46d93117eaf5/artifacts/0omkgoji_unnamed.png" 
    alt="Mercado Livre" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

// Ícone do Shopee
const ShopeeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C9.243 2 7 4.243 7 7h2c0-1.654 1.346-3 3-3s3 1.346 3 3h2c0-2.757-2.243-5-5-5zM5 8v14h14V8H5zm7 10c-2.206 0-4-1.794-4-4h2c0 1.103.897 2 2 2s2-.897 2-2h2c0 2.206-1.794 4-4 4z"/>
  </svg>
);

const produtoSchema = z.object({
  nome: z.string().min(1, "Nome obrigatório"),
  descricao: z.string().optional().default(""),
  preco: z.coerce.number().min(0, "Preço inválido").default(0),
  imagem_url: z.string().optional().default(""),
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

export default function PerfilPage() {
  const { authAxios } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [produtoDialogOpen, setProdutoDialogOpen] = useState(false);
  const [editingProduto, setEditingProduto] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  
  // Cupons state
  const [cupons, setCupons] = useState([]);
  const [cupomDialogOpen, setCupomDialogOpen] = useState(false);
  const [editingCupom, setEditingCupom] = useState(null);
  const [cupomForm, setCupomForm] = useState({
    codigo: "",
    desconto_percentual: 0,
    desconto_valor: 0,
    validade_inicio: "",
    validade_fim: "",
    ativo: true
  });

  // Form states
  const [nomeNegocio, setNomeNegocio] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [imagemFundoUrl, setImagemFundoUrl] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [whatsappAtivo, setWhatsappAtivo] = useState(true);
  const [instagram, setInstagram] = useState("");
  const [instagramAtivo, setInstagramAtivo] = useState(true);
  const [telegram, setTelegram] = useState("");
  const [telegramAtivo, setTelegramAtivo] = useState(true);
  const [facebook, setFacebook] = useState("");
  const [facebookAtivo, setFacebookAtivo] = useState(true);
  const [tiktok, setTiktok] = useState("");
  const [tiktokAtivo, setTiktokAtivo] = useState(true);
  const [youtube, setYoutube] = useState("");
  const [youtubeAtivo, setYoutubeAtivo] = useState(true);
  const [mercadopago, setMercadopago] = useState("");
  const [mercadopagoAtivo, setMercadopagoAtivo] = useState(true);
  const [shopee, setShopee] = useState("");
  const [shopeeAtivo, setShopeeAtivo] = useState(true);
  const [produtos, setProdutos] = useState([]);

  const produtoForm = useForm({
    resolver: zodResolver(produtoSchema),
    defaultValues: { nome: "", descricao: "", preco: 0, imagem_url: "" },
  });

  useEffect(() => {
    fetchPerfil();
  }, []);

  const fetchPerfil = async () => {
    try {
      const res = await authAxios.get("/perfil");
      setPerfil(res.data);
      setNomeNegocio(res.data.nome_negocio || "");
      setLogoUrl(res.data.logo_url || "");
      setImagemFundoUrl(res.data.imagem_fundo_url || "");
      setWhatsapp(res.data.whatsapp || "");
      setWhatsappAtivo(res.data.whatsapp_ativo !== false);
      setInstagram(res.data.instagram || "");
      setInstagramAtivo(res.data.instagram_ativo !== false);
      setTelegram(res.data.telegram || "");
      setTelegramAtivo(res.data.telegram_ativo !== false);
      setFacebook(res.data.facebook || "");
      setFacebookAtivo(res.data.facebook_ativo !== false);
      setTiktok(res.data.tiktok || "");
      setTiktokAtivo(res.data.tiktok_ativo !== false);
      setYoutube(res.data.youtube || "");
      setYoutubeAtivo(res.data.youtube_ativo !== false);
      setMercadopago(res.data.mercadopago || "");
      setMercadopagoAtivo(res.data.mercadopago_ativo !== false);
      setShopee(res.data.shopee || "");
      setShopeeAtivo(res.data.shopee_ativo !== false);
      setProdutos(res.data.produtos || []);
      // Fetch cupons
      fetchCupons();
    } catch (err) {
      toast.error("Erro ao carregar perfil");
    } finally {
      setLoading(false);
    }
  };

  // Cupons functions
  const fetchCupons = async () => {
    try {
      const res = await authAxios.get("/cupons");
      setCupons(res.data);
    } catch (err) {
      console.error("Erro ao carregar cupons", err);
    }
  };

  const handleOpenCupomDialog = (cupom = null) => {
    if (cupom) {
      setEditingCupom(cupom);
      setCupomForm({
        codigo: cupom.codigo,
        desconto_percentual: cupom.desconto_percentual,
        desconto_valor: cupom.desconto_valor,
        validade_inicio: cupom.validade_inicio.split("T")[0],
        validade_fim: cupom.validade_fim.split("T")[0],
        ativo: cupom.ativo
      });
    } else {
      setEditingCupom(null);
      setCupomForm({
        codigo: "",
        desconto_percentual: 0,
        desconto_valor: 0,
        validade_inicio: new Date().toISOString().split("T")[0],
        validade_fim: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        ativo: true
      });
    }
    setCupomDialogOpen(true);
  };

  const handleSaveCupom = async () => {
    if (!cupomForm.codigo.trim()) {
      toast.error("Código do cupom é obrigatório");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...cupomForm,
        validade_inicio: new Date(cupomForm.validade_inicio).toISOString(),
        validade_fim: new Date(cupomForm.validade_fim + "T23:59:59").toISOString()
      };
      
      if (editingCupom) {
        await authAxios.put(`/cupons/${editingCupom.id}`, payload);
        toast.success("Cupom atualizado!");
      } else {
        await authAxios.post("/cupons", payload);
        toast.success("Cupom criado!");
      }
      setCupomDialogOpen(false);
      fetchCupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar cupom");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCupom = async (cupomId) => {
    if (!window.confirm("Excluir este cupom?")) return;
    try {
      await authAxios.delete(`/cupons/${cupomId}`);
      toast.success("Cupom excluído!");
      fetchCupons();
    } catch (err) {
      toast.error("Erro ao excluir cupom");
    }
  };

  const toggleCupomAtivo = async (cupom) => {
    try {
      await authAxios.put(`/cupons/${cupom.id}`, { ativo: !cupom.ativo });
      fetchCupons();
      toast.success(cupom.ativo ? "Cupom desativado" : "Cupom ativado");
    } catch (err) {
      toast.error("Erro ao atualizar cupom");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authAxios.put("/perfil", {
        nome_negocio: nomeNegocio,
        logo_url: logoUrl,
        imagem_fundo_url: imagemFundoUrl,
        whatsapp,
        whatsapp_ativo: whatsappAtivo,
        instagram,
        instagram_ativo: instagramAtivo,
        telegram,
        telegram_ativo: telegramAtivo,
        facebook,
        facebook_ativo: facebookAtivo,
        tiktok,
        tiktok_ativo: tiktokAtivo,
        youtube,
        youtube_ativo: youtubeAtivo,
        mercadopago,
        mercadopago_ativo: mercadopagoAtivo,
        shopee,
        shopee_ativo: shopeeAtivo,
        produtos
      });
      setPerfil(res.data);
      toast.success("Perfil salvo com sucesso!");
    } catch (err) {
      toast.error("Erro ao salvar perfil");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!perfil?.token_publico) {
      // Gera token se não existir
      try {
        const res = await authAxios.post("/perfil/gerar-token");
        setPerfil({ ...perfil, token_publico: res.data.token_publico });
        const link = `${window.location.origin}/perfil-publico/${res.data.token_publico}`;
        await navigator.clipboard.writeText(link);
        toast.success("Link copiado!");
      } catch (err) {
        toast.error("Erro ao gerar link");
      }
    } else {
      const link = `${window.location.origin}/perfil-publico/${perfil.token_publico}`;
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado para a área de transferência!");
    }
  };

  const handlePreview = () => {
    if (perfil?.token_publico) {
      window.open(`/perfil-publico/${perfil.token_publico}`, "_blank");
    } else {
      toast.error("Salve o perfil primeiro para gerar o link");
    }
  };

  // Produto handlers
  const handleOpenProdutoDialog = (produto = null, index = null) => {
    if (produto) {
      setEditingProduto(produto);
      setEditingIndex(index);
      produtoForm.reset({
        nome: produto.nome,
        descricao: produto.descricao || "",
        preco: produto.preco || 0,
        imagem_url: produto.imagem_url || "",
      });
    } else {
      setEditingProduto(null);
      setEditingIndex(null);
      produtoForm.reset({ nome: "", descricao: "", preco: 0, imagem_url: "" });
    }
    setProdutoDialogOpen(true);
  };

  // Persiste APENAS produtos no backend (usa PATCH-like via PUT parcial)
  const persistirProdutos = async (novosProdutos) => {
    try {
      const res = await authAxios.put("/perfil", { produtos: novosProdutos });
      setPerfil(res.data);
      return true;
    } catch (err) {
      toast.error("Erro ao salvar produto no servidor");
      return false;
    }
  };

  const handleSubmitProduto = async (data) => {
    let novosProdutos;
    if (editingIndex !== null) {
      // Editando
      novosProdutos = [...produtos];
      novosProdutos[editingIndex] = { ...novosProdutos[editingIndex], ...data };
    } else {
      // Adicionando
      if (produtos.length >= 10) {
        toast.error("Máximo de 10 produtos atingido");
        return;
      }
      novosProdutos = [...produtos, { id: "", ...data }];
    }
    setProdutos(novosProdutos);
    setProdutoDialogOpen(false);
    // Persiste imediatamente para nao depender do botao global "Salvar"
    const ok = await persistirProdutos(novosProdutos);
    if (ok) {
      toast.success(editingIndex !== null ? "Produto atualizado e salvo!" : "Produto adicionado e salvo!");
    }
  };

  const handleDeleteProduto = async (index) => {
    if (!window.confirm("Excluir este produto?")) return;
    const novosProdutos = produtos.filter((_, i) => i !== index);
    setProdutos(novosProdutos);
    const ok = await persistirProdutos(novosProdutos);
    if (ok) toast.success("Produto removido!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="perfil-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Meu Perfil</h2>
          <p className="text-muted-foreground">
            Configure seu perfil público para compartilhar com clientes
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePreview} data-testid="preview-btn">
            <Eye className="w-4 h-4 mr-2" />
            Visualizar
          </Button>
          <Button variant="outline" onClick={handleCopyLink} data-testid="copy-link-btn">
            <Link2 className="w-4 h-4 mr-2" />
            Copiar Link
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
            {saving ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informações Básicas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Informações do Negócio
            </CardTitle>
            <CardDescription>
              Dados que aparecerão na sua página pública
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nomeNegocio">Nome do Negócio</Label>
              <Input
                id="nomeNegocio"
                placeholder="Ex: Minha Loja"
                value={nomeNegocio}
                onChange={(e) => setNomeNegocio(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logoUrl">URL da Logo/Foto</Label>
              <Input
                id="logoUrl"
                placeholder="https://exemplo.com/logo.png"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
              />
              {logoUrl && (
                <div className="mt-2 p-2 bg-muted rounded-lg">
                  <img 
                    src={logoUrl} 
                    alt="Logo preview" 
                    className="h-20 w-20 object-cover rounded-full mx-auto"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="imagemFundoUrl">URL da Imagem de Fundo</Label>
              <Input
                id="imagemFundoUrl"
                placeholder="https://exemplo.com/fundo.png"
                value={imagemFundoUrl}
                onChange={(e) => setImagemFundoUrl(e.target.value)}
              />
              {imagemFundoUrl && (
                <div className="mt-2 p-2 bg-muted rounded-lg">
                  <img 
                    src={imagemFundoUrl} 
                    alt="Fundo preview" 
                    className="h-24 w-full object-cover rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Redes Sociais */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Redes Sociais
            </CardTitle>
            <CardDescription>
              Links para seus clientes entrarem em contato. Use o switch para ativar/desativar.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* WhatsApp */}
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="whatsapp" className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-500" />
                  WhatsApp
                </Label>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${whatsappAtivo ? 'text-green-500' : 'text-muted-foreground'}`}>
                    {whatsappAtivo ? 'Ativo' : 'Inativo'}
                  </span>
                  <Switch
                    checked={whatsappAtivo}
                    onCheckedChange={setWhatsappAtivo}
                  />
                </div>
              </div>
              <Input
                id="whatsapp"
                placeholder="5511999999999"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                disabled={!whatsappAtivo}
                className={!whatsappAtivo ? 'opacity-50' : ''}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Formato: 55 + DDD + número
              </p>
            </div>

            {/* Instagram */}
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="instagram" className="flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-pink-500" />
                  Instagram
                </Label>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${instagramAtivo ? 'text-green-500' : 'text-muted-foreground'}`}>
                    {instagramAtivo ? 'Ativo' : 'Inativo'}
                  </span>
                  <Switch
                    checked={instagramAtivo}
                    onCheckedChange={setInstagramAtivo}
                  />
                </div>
              </div>
              <Input
                id="instagram"
                placeholder="seu_usuario"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                disabled={!instagramAtivo}
                className={!instagramAtivo ? 'opacity-50' : ''}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Apenas o nome de usuário, sem @
              </p>
            </div>

            {/* Telegram */}
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="telegram" className="flex items-center gap-2">
                  <Send className="w-4 h-4 text-blue-500" />
                  Telegram
                </Label>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${telegramAtivo ? 'text-green-500' : 'text-muted-foreground'}`}>
                    {telegramAtivo ? 'Ativo' : 'Inativo'}
                  </span>
                  <Switch
                    checked={telegramAtivo}
                    onCheckedChange={setTelegramAtivo}
                  />
                </div>
              </div>
              <Input
                id="telegram"
                placeholder="seu_usuario"
                value={telegram}
                onChange={(e) => setTelegram(e.target.value)}
                disabled={!telegramAtivo}
                className={!telegramAtivo ? 'opacity-50' : ''}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Apenas o nome de usuário, sem @
              </p>
            </div>

            {/* Facebook */}
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="facebook" className="flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-blue-600" />
                  Facebook
                </Label>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${facebookAtivo ? 'text-green-500' : 'text-muted-foreground'}`}>
                    {facebookAtivo ? 'Ativo' : 'Inativo'}
                  </span>
                  <Switch
                    checked={facebookAtivo}
                    onCheckedChange={setFacebookAtivo}
                  />
                </div>
              </div>
              <Input
                id="facebook"
                placeholder="sua_pagina ou perfil"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                disabled={!facebookAtivo}
                className={!facebookAtivo ? 'opacity-50' : ''}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Nome da página ou perfil
              </p>
            </div>

            {/* TikTok */}
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="tiktok" className="flex items-center gap-2">
                  <TikTokIcon className="w-4 h-4" />
                  TikTok
                </Label>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${tiktokAtivo ? 'text-green-500' : 'text-muted-foreground'}`}>
                    {tiktokAtivo ? 'Ativo' : 'Inativo'}
                  </span>
                  <Switch
                    checked={tiktokAtivo}
                    onCheckedChange={setTiktokAtivo}
                  />
                </div>
              </div>
              <Input
                id="tiktok"
                placeholder="seu_usuario"
                value={tiktok}
                onChange={(e) => setTiktok(e.target.value)}
                disabled={!tiktokAtivo}
                className={!tiktokAtivo ? 'opacity-50' : ''}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Apenas o nome de usuário, sem @
              </p>
            </div>

            {/* YouTube */}
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="youtube" className="flex items-center gap-2">
                  <Youtube className="w-4 h-4 text-red-500" />
                  YouTube
                </Label>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${youtubeAtivo ? 'text-green-500' : 'text-muted-foreground'}`}>
                    {youtubeAtivo ? 'Ativo' : 'Inativo'}
                  </span>
                  <Switch
                    checked={youtubeAtivo}
                    onCheckedChange={setYoutubeAtivo}
                  />
                </div>
              </div>
              <Input
                id="youtube"
                placeholder="@seu_canal ou nome do canal"
                value={youtube}
                onChange={(e) => setYoutube(e.target.value)}
                disabled={!youtubeAtivo}
                className={!youtubeAtivo ? 'opacity-50' : ''}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Nome do canal ou @handle
              </p>
            </div>

            {/* Mercado Livre */}
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="mercadopago" className="flex items-center gap-2">
                  <MercadoLivreIcon className="w-5 h-5 rounded" />
                  Mercado Livre
                </Label>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${mercadopagoAtivo ? 'text-green-500' : 'text-muted-foreground'}`}>
                    {mercadopagoAtivo ? 'Ativo' : 'Inativo'}
                  </span>
                  <Switch
                    checked={mercadopagoAtivo}
                    onCheckedChange={setMercadopagoAtivo}
                  />
                </div>
              </div>
              <Input
                id="mercadopago"
                placeholder="Link da sua loja no Mercado Livre"
                value={mercadopago}
                onChange={(e) => setMercadopago(e.target.value)}
                disabled={!mercadopagoAtivo}
                className={!mercadopagoAtivo ? 'opacity-50' : ''}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Cole o link completo da sua loja
              </p>
            </div>

            {/* Shopee */}
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="shopee" className="flex items-center gap-2">
                  <ShopeeIcon className="w-4 h-4 text-orange-500" />
                  Shopee
                </Label>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${shopeeAtivo ? 'text-green-500' : 'text-muted-foreground'}`}>
                    {shopeeAtivo ? 'Ativo' : 'Inativo'}
                  </span>
                  <Switch
                    checked={shopeeAtivo}
                    onCheckedChange={setShopeeAtivo}
                  />
                </div>
              </div>
              <Input
                id="shopee"
                placeholder="Link da sua loja na Shopee"
                value={shopee}
                onChange={(e) => setShopee(e.target.value)}
                disabled={!shopeeAtivo}
                className={!shopeeAtivo ? 'opacity-50' : ''}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Cole o link completo da sua loja
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Produtos em Destaque */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Produtos em Destaque
              </CardTitle>
              <CardDescription>
                Adicione até 10 produtos para exibir na sua página ({produtos.length}/10)
              </CardDescription>
            </div>
            <Button 
              onClick={() => handleOpenProdutoDialog()} 
              disabled={produtos.length >= 10}
              data-testid="add-produto-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Produto
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {produtos.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum produto cadastrado</p>
              <p className="text-sm">Clique em "Adicionar Produto" para começar</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {produtos.map((produto, index) => (
                <Card key={index} className="overflow-hidden">
                  {produto.imagem_url && (
                    <div className="h-32 bg-muted">
                      <img 
                        src={produto.imagem_url} 
                        alt={produto.nome}
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.parentElement.style.display = 'none'}
                      />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <h4 className="font-semibold truncate">{produto.nome}</h4>
                    {produto.descricao && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {produto.descricao}
                      </p>
                    )}
                    {produto.preco > 0 && (
                      <p className="text-lg font-bold text-primary mt-2">
                        {formatCurrency(produto.preco)}
                      </p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleOpenProdutoDialog(produto, index)}
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteProduto(index)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cupons de Desconto */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <CardTitle className="text-white">Cupons de Desconto</CardTitle>
                <CardDescription>Crie cupons para seus clientes</CardDescription>
              </div>
            </div>
            <Button onClick={() => handleOpenCupomDialog()} size="sm" className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Novo Cupom
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {cupons.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Ticket className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum cupom criado</p>
              <p className="text-sm">Crie cupons de desconto para seus clientes</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cupons.map((cupom) => {
                const agora = new Date();
                const inicio = new Date(cupom.validade_inicio);
                const fim = new Date(cupom.validade_fim);
                const valido = cupom.ativo && agora >= inicio && agora <= fim;
                const expirado = agora > fim;
                
                return (
                  <div key={cupom.id} className={`p-4 rounded-lg border ${valido ? 'bg-purple-500/10 border-purple-500/30' : expirado ? 'bg-red-500/10 border-red-500/30' : 'bg-slate-700/50 border-slate-600'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1.5 rounded-lg font-mono font-bold ${valido ? 'bg-purple-500 text-white' : expirado ? 'bg-red-500/50 text-red-200' : 'bg-slate-600 text-slate-300'}`}>
                          {cupom.codigo}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {cupom.desconto_percentual > 0 && `${cupom.desconto_percentual}% de desconto`}
                            {cupom.desconto_percentual > 0 && cupom.desconto_valor > 0 && " + "}
                            {cupom.desconto_valor > 0 && `${formatCurrency(cupom.desconto_valor)} de desconto`}
                          </p>
                          <p className="text-xs text-slate-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(cupom.validade_inicio).toLocaleDateString("pt-BR")} até {new Date(cupom.validade_fim).toLocaleDateString("pt-BR")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded ${valido ? 'bg-green-500/20 text-green-400' : expirado ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {valido ? 'Ativo' : expirado ? 'Expirado' : 'Inativo'}
                        </span>
                        <Switch
                          checked={cupom.ativo}
                          onCheckedChange={() => toggleCupomAtivo(cupom)}
                        />
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenCupomDialog(cupom)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300" onClick={() => handleDeleteCupom(cupom.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cupom Dialog */}
      <Dialog open={cupomDialogOpen} onOpenChange={setCupomDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCupom ? "Editar Cupom" : "Novo Cupom"}
            </DialogTitle>
            <DialogDescription>
              Configure o cupom de desconto
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="codigo">Código do Cupom *</Label>
              <Input
                id="codigo"
                placeholder="Ex: NEX10, DESCONTO20"
                value={cupomForm.codigo}
                onChange={(e) => setCupomForm({...cupomForm, codigo: e.target.value.toUpperCase()})}
                className="font-mono uppercase"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="desconto_percentual">Desconto %</Label>
                <Input
                  id="desconto_percentual"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="Ex: 10"
                  value={cupomForm.desconto_percentual}
                  onChange={(e) => setCupomForm({...cupomForm, desconto_percentual: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="desconto_valor">Desconto R$</Label>
                <Input
                  id="desconto_valor"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Ex: 5.00"
                  value={cupomForm.desconto_valor}
                  onChange={(e) => setCupomForm({...cupomForm, desconto_valor: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="validade_inicio">Válido de</Label>
                <Input
                  id="validade_inicio"
                  type="date"
                  value={cupomForm.validade_inicio}
                  onChange={(e) => setCupomForm({...cupomForm, validade_inicio: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="validade_fim">Válido até</Label>
                <Input
                  id="validade_fim"
                  type="date"
                  value={cupomForm.validade_fim}
                  onChange={(e) => setCupomForm({...cupomForm, validade_fim: e.target.value})}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <Label htmlFor="ativo">Cupom ativo</Label>
              <Switch
                id="ativo"
                checked={cupomForm.ativo}
                onCheckedChange={(checked) => setCupomForm({...cupomForm, ativo: checked})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCupomDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveCupom} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? "Salvando..." : editingCupom ? "Salvar" : "Criar Cupom"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Produto Dialog */}
      <Dialog open={produtoDialogOpen} onOpenChange={setProdutoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingProduto ? "Editar Produto" : "Novo Produto"}
            </DialogTitle>
            <DialogDescription>
              Preencha as informações do produto
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={produtoForm.handleSubmit(handleSubmitProduto)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome do Produto *</Label>
              <Input
                id="nome"
                placeholder="Nome do produto"
                {...produtoForm.register("nome")}
              />
              {produtoForm.formState.errors.nome && (
                <p className="text-sm text-destructive">
                  {produtoForm.formState.errors.nome.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                placeholder="Descrição do produto"
                {...produtoForm.register("descricao")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="preco">Preço (R$)</Label>
              <Input
                id="preco"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...produtoForm.register("preco")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="imagem_url">URL da Imagem</Label>
              <Input
                id="imagem_url"
                placeholder="https://exemplo.com/imagem.png"
                {...produtoForm.register("imagem_url")}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setProdutoDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingProduto ? "Atualizar" : "Adicionar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
